import React, { useState, useEffect } from 'react';
import {
  Button, Card, Space, Typography, Tag as AntdTag,
  Row, Col, Input, Spin, Empty, message, Modal, Form
} from 'antd';
import {
  TagOutlined, SyncOutlined
} from '@ant-design/icons';
import { EditOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Title } = Typography;

// 标签服务
const tagService = {
  // 同步微信标签
  syncTags: async () => {
    try {
      // 从localStorage获取tenant对象
      const tenantStr = localStorage.getItem('tenant');
      let tenantId = '0';

      try {
        if (tenantStr) {
          const tenant = JSON.parse(tenantStr);
          tenantId = tenant.id.toString();
        }
      } catch (error) {
        console.error('解析tenant信息失败:', error);
      }

      const sourceType = 'WEWORK'; // 企业微信类型
      console.log('同步标签参数:', { tenantId, sourceType });
      const response = await api.post('/sync/tags/async', null, {
        params: { tenantId, sourceType }
      });
      return response.data;
    } catch (error) {
      console.error('同步标签失败:', error);
      throw error;
    }
  },

  // 获取标签组列表及其所有标签
  getAllTagGroupsWithTags: async () => {
    try {
      // 1. 获取所有标签组
      const groupsResponse = await api.get('/tags/groups', {
        params: { page: 0, size: 100 }
      });

      let tagGroups = [];

      if (groupsResponse.data) {
        if (groupsResponse.data.content) {
          tagGroups = groupsResponse.data.content;
        } else if (Array.isArray(groupsResponse.data)) {
          tagGroups = groupsResponse.data;
        }
      }

      // 2. 对每个标签组，获取其详细信息（包含标签列表）
      const tagGroupsWithTags = await Promise.all(
        tagGroups.map(async (group) => {
          try {
            const groupDetailResponse = await api.get(`/tags/groups/${group.id}`);
            return groupDetailResponse.data;
          } catch (error) {
            console.error(`获取标签组详情失败 (ID: ${group.id}):`, error);
            return { tagGroup: group, tags: [] };
          }
        })
      );

      console.log('获取所有标签组及标签:', tagGroupsWithTags);
      return tagGroupsWithTags;
    } catch (error) {
      console.error('获取标签组及标签失败:', error);
      throw error;
    }
  },
  // 创建企微标签
  createWeworkTag: async (payload) => {
    const response = await api.post('/tags/wework/tags', payload);
    return response.data;
  },
  // 更新企微标签
  updateWeworkTag: async (id, payload) => {
    const response = await api.put(`/tags/wework/tags/${id}`, payload);
    return response.data;
  },
  // 删除企微标签
  deleteWeworkTag: async (id) => {
    const response = await api.delete(`/tags/wework/tags/${id}`);
    return response.data;
  },
  // 更新企微标签组
  updateWeworkGroup: async (id, payload) => {
    const response = await api.put(`/tags/wework/groups/${id}`, payload);
    return response.data;
  },
  // 删除企微标签组
  deleteWeworkGroup: async (id) => {
    const response = await api.delete(`/tags/wework/groups/${id}`);
    return response.data;
  }
};

const Tags = () => {
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [tagGroups, setTagGroups] = useState([]); // 标签组数据，每个组包含标签
  const [saving, setSaving] = useState(false);
  // const { tenant } = useAuth(); // 从认证上下文获取当前租户信息

  // 统一编辑弹窗状态
  const [groupEditor, setGroupEditor] = useState({
    visible: false,
    group: null,
    groupName: '',
    tagsDraft: [], // { id?, name, originalName, isNew, deleted }
    isCreate: false
  });

  // 加载所有标签组及其标签
  const fetchAllTagGroupsWithTags = async () => {
    try {
      setLoading(true);
      console.log('开始获取所有标签组及标签...');
      const result = await tagService.getAllTagGroupsWithTags();

      // 排序：先排序每个组内的标签，再排序组
      const sorted = (result || [])
        .map(gd => {
          const tags = Array.isArray(gd.tags) ? [...gd.tags] : [];
          const sortedTags = tags.sort((a, b) => {
            const ao = (a && typeof a.orderId === 'number') ? a.orderId : 0;
            const bo = (b && typeof b.orderId === 'number') ? b.orderId : 0;
            return bo - ao;
          });
          if (gd.tagGroup) {
            return { ...gd, tags: sortedTags };
          }
          return { ...gd, tags: sortedTags };
        })
        .sort((a, b) => {
          const ag = a.tagGroup || a;
          const bg = b.tagGroup || b;
          const ao = (ag && typeof ag.orderId === 'number') ? ag.orderId : 0;
          const bo = (bg && typeof bg.orderId === 'number') ? bg.orderId : 0;
          return bo - ao;
        });

      setTagGroups(sorted);
    } catch (error) {
      console.error('获取标签组及标签失败:', error);
      message.error('获取标签组及标签失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllTagGroupsWithTags();
  }, []);

  // 同步企业微信标签
  const handleSync = async () => {
    try {
      setSyncLoading(true);
      console.log('开始同步企业微信标签...');
      const result = await tagService.syncTags();
      console.log('同步响应:', result);

      if (result) {
        message.success(result || '标签同步操作已完成');
      } else {
        message.success('标签同步操作已完成');
      }

      // 延迟2秒后重新加载数据，给服务器一些处理时间
      setTimeout(() => {
        fetchAllTagGroupsWithTags();
      }, 2000);
    } catch (error) {
      console.error('同步标签失败:', error);
      message.error('同步标签失败: ' + (error.message || '未知错误'));
    } finally {
      setSyncLoading(false);
    }
  };

  // 处理搜索
  const handleSearch = (value) => {
    setSearchText(value);
    // 实现前端搜索过滤 - 待实现
  };

  // 渲染标签项
  const renderTagItem = (tag) => {
    const color = tag.source === 'WEWORK' ? 'blue' : 'default';

    return (
      <AntdTag
        key={tag.id}
        color={color}
        closable={false}
        style={{
          margin: '3px 8px 3px 0',
          padding: '2px 8px',
          fontSize: '12px',
          cursor: 'default'
        }}
        title={undefined}
      >
        <TagOutlined style={{ marginRight: 3 }} /> {tag.tagName || tag.name}
      </AntdTag>
    );
  };

  // 打开统一编辑弹窗
  const openGroupEditor = (groupData) => {
    const group = groupData.tagGroup || groupData;
    const tags = (groupData.tags || []).map(t => ({
      id: t.id,
      name: t.tagName || t.name || '',
      originalName: t.tagName || t.name || '',
      isNew: false,
      deleted: false,
      source: t.source
    }));
    setGroupEditor({
      visible: true,
      group,
      groupName: group.groupName || '',
      tagsDraft: tags,
      isCreate: false
    });
  };

  const openCreateGroupEditor = () => {
    setGroupEditor({
      visible: true,
      group: null,
      groupName: '',
      tagsDraft: [{ id: null, name: '', originalName: '', isNew: true, deleted: false, source: 'WEWORK' }],
      isCreate: true
    });
  };

  const handleGroupEditorAddTag = () => {
    setGroupEditor(prev => ({
      ...prev,
      tagsDraft: [
        ...prev.tagsDraft,
        { id: null, name: '', originalName: '', isNew: true, deleted: false, source: 'WEWORK' }
      ]
    }));
  };

  const handleGroupEditorChangeTagName = (index, value) => {
    setGroupEditor(prev => {
      const next = [...prev.tagsDraft];
      next[index] = { ...next[index], name: value };
      return { ...prev, tagsDraft: next };
    });
  };

  const handleGroupEditorDeleteTag = (index) => {
    setGroupEditor(prev => {
      const current = prev.tagsDraft[index];
      if (current.isNew) {
        const next = prev.tagsDraft.filter((_, i) => i !== index);
        return { ...prev, tagsDraft: next };
      }
      const next = [...prev.tagsDraft];
      next[index] = { ...next[index], deleted: true };
      return { ...prev, tagsDraft: next };
    });
  };

  const handleGroupEditorSave = async () => {
    if (!groupEditor.group) return;
    try {
      setSaving(true);
      const groupId = groupEditor.group.id;
      const operations = [];

      const trimmedGroupName = (groupEditor.groupName || '').trim();
      const originalGroupName = groupEditor.group.groupName || '';
      if (trimmedGroupName && trimmedGroupName !== originalGroupName) {
        operations.push(async () => {
          await tagService.updateWeworkGroup(groupId, { name: trimmedGroupName });
        });
      }

      const creates = [];
      const updates = [];
      const deletes = [];

      for (const draft of groupEditor.tagsDraft) {
        const trimmedName = (draft.name || '').trim();
        if (draft.deleted && draft.id) {
          deletes.push(async () => {
            await tagService.deleteWeworkTag(draft.id);
          });
        } else if (draft.isNew) {
          if (trimmedName) {
            creates.push(async () => {
              return await tagService.createWeworkTag({ tagName: trimmedName, groupId });
            });
          }
        } else if (draft.id && trimmedName !== draft.originalName) {
          updates.push(async () => {
            await tagService.updateWeworkTag(draft.id, { name: trimmedName });
          });
        }
      }

      for (const op of operations) { await op(); }
      for (const op of deletes) { await op(); }
      for (const op of creates) { await op(); }
      for (const op of updates) { await op(); }

      await fetchAllTagGroupsWithTags();

      message.success('已保存修改');
      setGroupEditor({ visible: false, group: null, groupName: '', tagsDraft: [], isCreate: false });
    } catch (err) {
      message.error(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateGroupSave = async () => {
    try {
      setSaving(true);
      const trimmedGroupName = (groupEditor.groupName || '').trim();
      const tagNames = groupEditor.tagsDraft
        .filter(t => !t.deleted && (t.name || '').trim())
        .map(t => (t.name || '').trim());
      if (!trimmedGroupName) {
        message.error('请输入标签组名称');
        return;
      }
      if (tagNames.length === 0) {
        message.error('请至少添加一个标签');
        return;
      }

      const created = [];
      let createdGroupId = null;

      for (let i = 0; i < tagNames.length; i += 1) {
        const name = tagNames[i];
        const payload = createdGroupId
          ? { tagName: name, groupId: createdGroupId }
          : { tagName: name, groupName: trimmedGroupName };
        const res = await tagService.createWeworkTag(payload);
        const createdTag = res && (res.data || res);
        created.push(createdTag);
        // try to infer group id from response
        if (!createdGroupId) {
          createdGroupId = createdTag?.groupId || createdTag?.group?.id || createdTag?.tagGroupId || null;
        }
      }

      await fetchAllTagGroupsWithTags();

      message.success('已创建标签组');
      setGroupEditor({ visible: false, group: null, groupName: '', tagsDraft: [], isCreate: false });
    } catch (err) {
      message.error(err.message || '创建失败');
    } finally {
      setSaving(false);
    }
  };

  const handleGroupEditorDeleteGroup = async () => {
    if (!groupEditor.group) return;
    const groupId = groupEditor.group.id;
    Modal.confirm({
      title: '确认删除该标签组及其下企微标签？',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          setSaving(true);
          await tagService.deleteWeworkGroup(groupId);
          await fetchAllTagGroupsWithTags();
          setGroupEditor({ visible: false, group: null, groupName: '', tagsDraft: [] });
          message.success('已删除标签组');
        } catch (err) {
          message.error(err.message || '删除失败');
        } finally {
          setSaving(false);
        }
      }
    });
  };

  // 渲染标签组
  const renderTagGroups = () => {
    // 检查是否有标签组数据
    if (!tagGroups || tagGroups.length === 0) {
      return <Empty description="暂无标签组数据" />;
    }

    return (
      <div className="tag-groups-container">
        {tagGroups.map(groupData => {
          const group = groupData.tagGroup || groupData;
          const tags = groupData.tags || [];

          // 如果没有标签，可能展示一个空状态
          if (!tags || tags.length === 0) {
            return (
              <div className="tag-group" key={group.id}>
                <div className="tag-group-header">
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Space>
                        <span className="tag-group-title">{group.groupName || '未命名组'}</span>
                        <AntdTag color="blue">{tags.length || 0} 个标签</AntdTag>
                        {group.source === 'WEWORK' && (
                          <AntdTag color="processing">企微</AntdTag>
                        )}
                      </Space>
                    </Col>
                    <Col>
                      <Space size={8}>
                        <Button size="small" type="link" icon={<EditOutlined />} disabled={group.source !== 'WEWORK'} onClick={() => openGroupEditor(groupData)}>编辑</Button>
                      </Space>
                    </Col>
                  </Row>
                </div>
                <Empty description="该标签组下暂无标签" />
              </div>
            );
          }

          return (
            <div className="tag-group" key={group.id}>
              <div className="tag-group-header">
                <Row justify="space-between" align="middle">
                  <Col>
                    <Space>
                      <span className="tag-group-title">{group.groupName || '未命名组'}</span>
                      <AntdTag color="blue">{tags.length || 0} 个标签</AntdTag>
                      {group.source === 'WEWORK' && (
                        <AntdTag color="processing">企微</AntdTag>
                      )}
                    </Space>
                  </Col>
                  <Col>
                    <Space size={8}>
                      <Button size="small" type="link" icon={<EditOutlined />} disabled={group.source !== 'WEWORK'} onClick={() => openGroupEditor(groupData)}>编辑</Button>
                    </Space>
                  </Col>
                </Row>
              </div>
              <div className="tag-container">
                {tags.map(tag => renderTagItem(tag))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5}>标签管理</Title>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Space>
                <Input.Search
                  placeholder="搜索标签"
                  allowClear
                  enterButton
                  onSearch={handleSearch}
                  style={{ width: 200 }}
                />
              </Space>
            </Col>
          </Row>
          <Row>
            <Col>
              <Space>
                <Button
                  icon={<PlusOutlined />}
                  onClick={openCreateGroupEditor}
                  type='primary'
                >
                  新增标签组
                </Button>
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={handleSync}
                  loading={syncLoading}
                >
                  同步企业微信标签
                </Button>
                {/* 统一在编辑弹窗内新增标签 */}
              </Space>
            </Col>
          </Row>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '30px' }}>
            <Spin size="large" />
          </div>
        ) : (
          renderTagGroups()
        )}
      </Card>

      {/* 统一编辑标签组弹窗 */}
      <Modal
        title={groupEditor.isCreate ? '新建标签组' : '编辑标签组'}
        open={groupEditor.visible}
        onCancel={() => setGroupEditor({ visible: false, group: null, groupName: '', tagsDraft: [], isCreate: false })}
        footer={[
          groupEditor.isCreate ? (
            <Button
              key="create"
              type="primary"
              onClick={handleCreateGroupSave}
              loading={saving}
              disabled={!(groupEditor.groupName || '').trim() || groupEditor.tagsDraft.filter(t => !t.deleted && (t.name || '').trim()).length === 0}
            >创建</Button>
          ) : (
            <Button key="save" type="primary" onClick={handleGroupEditorSave} loading={saving}>保存</Button>
          ),
          !groupEditor.isCreate && (
            <Button key="delete" danger disabled={!groupEditor.group || groupEditor.group.source !== 'WEWORK'} onClick={handleGroupEditorDeleteGroup} style={{ float: 'left' }} icon={<DeleteOutlined />}>删除标签组</Button>
          ),
          <Button key="cancel" onClick={() => setGroupEditor({ visible: false, group: null, groupName: '', tagsDraft: [], isCreate: false })}>取消</Button>
        ]}
      >
        <Form layout="vertical">
          <Form.Item label="标签组名称" required>
            <Input
              placeholder="请输入标签组名称"
              value={groupEditor.groupName}
              onChange={(e) => setGroupEditor(prev => ({ ...prev, groupName: e.target.value }))}
            />
          </Form.Item>

          <Form.Item label="标签列表">
            {(groupEditor.tagsDraft || []).filter(t => !t.deleted).map((t, idx) => (
              <Row key={t.id || `new-${idx}`} gutter={8} style={{ marginBottom: 8 }} align="middle">
                <Col flex="auto">
                  <Input
                    placeholder="标签名称"
                    value={t.name}
                    onChange={(e) => handleGroupEditorChangeTagName(idx, e.target.value)}
                  />
                </Col>
                <Col>
                  <Button danger icon={<DeleteOutlined />} onClick={() => handleGroupEditorDeleteTag(idx)} />
                </Col>
              </Row>
            ))}
            <Button type="dashed" block icon={<PlusOutlined />} onClick={handleGroupEditorAddTag}>新增标签</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 旧版弹窗已移除：新建/编辑标签、编辑标签组 */}

      <style jsx global>{`
        .tag-groups-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .tag-group {
          border: 1px solid #f0f0f0;
          border-radius: 4px;
          overflow: hidden;
        }
        
        .tag-group-header {
          font-weight: bold;
          font-size: 16px;
          background: #f7f7f7;
          padding: 12px 16px;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .tag-group-title {
          color: #333;
        }
        
        .tag-container {
          padding: 16px;
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        
        .tag-container .ant-tag {
          line-height: 1.5;
        }
      `}</style>
    </div>
  );
};

export default Tags; 